import { useTranslation } from 'react-i18next'
import React, { useEffect, useMemo, useState } from 'react'
import { chooseDBTranslation, chooseDBTranslationIndex, isTranslateableObject } from 'utils/translations'
import { Schema, ValidationError } from 'yup'

import {
  AnyObject,
  ContextValue,
  DisabledFields,
  EditableFields,
  ErrorFields,
  TouchedFields,
  getFieldControlId as _getFieldControlId,
} from './types'
import { Context } from './context'
export type { NewOrExisting } from './types'

/**
 * Data edit context.
 */
const DataEditContext = <T extends AnyObject>({
  data,
  localeBase,
  onChange,
  schema,
  children,
  onSubmit,
  showAllErrors,
}: {
  data: T
  localeBase?: string
  onChange: (data: T) => unknown
  schema?: Schema<unknown>
  children: React.ReactNode
  onSubmit?: () => void
  /** Shows validation errors on all fields, not only touched ones (e.g. after a failed submit). */
  showAllErrors?: boolean
}) => {
  const { t, i18n } = useTranslation()
  const [errors, setErrors] = useState<ErrorFields<EditableFields<T>>>({})
  const [combinedErrors, setCombinedErrors] = useState<string[]>([])
  const [touched, setTouched] = useState<TouchedFields<EditableFields<T>>>({})
  const [allTouched, setAllTouched] = useState<boolean>(false)
  const [translationCacheBuster, setTranslationCacheBuster] = useState(0)
  const [errFields, setErrFields] = useState<string[]>([])

  useEffect(() => {
    if (showAllErrors) {
      setAllTouched(true)
    }
  }, [showAllErrors])

  const reloadTranslation = () => {
    setTranslationCacheBuster(translationCacheBuster + 1)
  }

  const translation = useMemo(() => {
    if (!isTranslateableObject(data)) {
      return null
    }

    return chooseDBTranslation(i18n, data)
  }, [data, i18n])

  const translationFields = useMemo(() => {
    const result: string[] = []

    if (!translation) {
      return result
    }

    for (const key in translation) {
      if (!['id', 'createdAt', 'updatedAt', 'languageId', 'Language'].includes(key)) {
        result.push(key)
      }
    }

    return result
  }, [translation])

  const hasTranslation = useMemo(() => {
    if (!isTranslateableObject(data)) {
      return false
    }

    return chooseDBTranslationIndex(i18n, data.translations) >= 0
  }, [data, i18n])

  const disabled: DisabledFields<EditableFields<T>> = useMemo(() => {
    if (hasTranslation) {
      return {}
    }

    const result: DisabledFields<EditableFields<T>> = {}
    for (const key of translationFields) {
      result[key as keyof DisabledFields<EditableFields<T>>] = true
    }
    return result
  }, [hasTranslation, translationFields])

  const flatData: EditableFields<T> = useMemo(
    () =>
      Object.assign(
        {},
        data,
        isTranslateableObject(data) ? chooseDBTranslation(i18n, data) : undefined
      ) as unknown as EditableFields<T>,
    [data, i18n]
  )

  useEffect(() => {
    if (!schema) {
      setErrors({})
      setCombinedErrors([])
      return
    }

    ;(async () => {
      try {
        await schema.validate(flatData, { abortEarly: false })
        {
          setErrors({})
          setCombinedErrors([])
        }
      } catch (err) {
        if (!(err instanceof ValidationError)) {
          throw err
        }

        const newErrors: typeof errors = {}

        for (const inner of err.inner) {
          const path = inner.path
          if (!path) {
            continue
          }

          const field = path as keyof EditableFields<T>
          if (allTouched || touched[field]) {
            newErrors[field] = inner.message
          }
        }
        {
          setErrors(newErrors)
          setCombinedErrors(err.errors)
          setErrFields(err.inner.map(item => item.path))
        }
      }
    })()
  }, [flatData, schema, allTouched, touched])

  const setFields = (change: { [key: string]: unknown }) => {
    let newData = data

    const newlyTouched: TouchedFields<EditableFields<T>> = {}

    for (const field in change) {
      const newValue = change[field]

      newlyTouched[field as keyof EditableFields<T>] = true

      if (isTranslateableObject(newData)) {
        let translationIndex = chooseDBTranslationIndex(i18n, newData.translations)
        if (translationIndex < 0) {
          translationIndex = 0
        }

        if (newData.translations.length && field in newData.translations[translationIndex]) {
          newData = {
            ...newData,
            translations: [
              ...newData.translations.slice(0, translationIndex),
              {
                ...newData.translations[translationIndex],
                [field]: newValue,
              },
              ...newData.translations.slice(translationIndex + 1),
            ],
          }

          continue
        }
      }

      newData = {
        ...newData,
        [field]: newValue,
      }
    }

    setTouched({
      ...touched,
      ...newlyTouched,
    })

    onChange(newData)
  }

  const Provider = (Context as unknown as React.Context<ContextValue<T>>).Provider

  const handleSubmit = (event: React.KeyboardEvent) => {
    if (onSubmit && (event.key === 'Enter' || event.key === 'NumpadEnter')) {
      event.preventDefault()
      {
        setAllTouched(true)
        if (combinedErrors.length === 0) {
          onSubmit()
        }
      }
    }
  }

  return (
    <Provider
      value={{
        data,
        valid: combinedErrors.length === 0,
        errors,
        combinedErrors,
        errFields,
        touched,
        disabled,
        allTouched,
        hasTranslation,
        flatData,
        setFields,
        onChange,
        reloadTranslation,
        handleSubmit,
        localeBase: localeBase ?? 'datacontext',
        t,
        i18n,
      }}
    >
      {children}
    </Provider>
  )
}

// Re-export getFieldControlId for backward compat
// Re-export getFieldControlId for backward compat
export const getFieldControlId = _getFieldControlId

export default DataEditContext
